// "Waiting on the owner" — the one rule that decides whether an item is work the
// customer still has to look at. The Content library, the daily text and the
// chat panel each used to answer this differently, so the same account could be
// told three different numbers.
//
// This is the TypeScript half of a pair with the Python side: the daily text
// is assembled in Python, where the equivalent filter is the named predicate
// `artifact_is_waiting_on_owner` in services/py/shared/models/artifact_types.py.
// It mirrors this file field-for-field — same type allowlist
// (OWNER_REVIEWABLE_ARTIFACT_TYPES / RENDERABLE_CONTENT_TYPES), same status
// set (WAITING_ON_OWNER_STATUSES on both sides), same skipped-in-setup check,
// and the same unactivated-campaign exclusion — so the daily text and the
// Content library report the same number today.
//
// The content check IS already shared, and is the part most easily broken:
// artifact_has_reviewable_content (services/py/shared/models/artifact_content.py,
// pinned by beacon's api/tests/test_artifact_content.py) has exactly two rules,
// and isUngeneratedHtmlEmail and isEmptyPlaintextEmail below are those two
// rules. The repos share no schema, so this note is the only thing holding them
// together — changing one side without the other reintroduces the disagreement.
// Same cross-repo sync arrangement as MONITOR_ARTIFACT_TYPES in
// ../types/artifact.ts.

import {
  isEmailHtml,
  isEmailPlaintext,
  isPost,
  type Artifact,
  type ArtifactType,
  type ContentArtifact,
  type ContentStatus,
} from "../types/artifact";
import type { Process } from "../types/process";

// The artifact types a customer can actually be shown and asked to approve.
// Everything else an agent produces — DMs, comment replies, proposed edits,
// monitors, deferred-content placeholders — either runs on its own or is
// reviewed somewhere other than the content surfaces, so counting it as waiting
// work inflates the number the customer sees.
export const RENDERABLE_CONTENT_TYPES = new Set<ArtifactType>([
  "instagram_post",
  "digital_ad",
  "email_plaintext",
  "email_html",
]);

// Both values are live in the data: campaigns create items as "draft", and an
// agent submitting a one-off item for approval writes "pending". Anything past
// these two has already been acted on.
export const WAITING_ON_OWNER_STATUSES = new Set<ContentStatus>([
  "draft",
  "pending",
]);

export type RenderableContentArtifact = Extract<
  ContentArtifact,
  {
    type: "instagram_post" | "digital_ad" | "email_plaintext" | "email_html";
  }
>;

export function isRenderableContentArtifact(
  artifact: Artifact,
): artifact is RenderableContentArtifact {
  return RENDERABLE_CONTENT_TYPES.has(artifact.type);
}

// Every caller here hands over raw db-api JSON cast to Artifact, with no schema
// validation in between, so `content` can be absent or null however the type
// reads. An unfilled content field means nothing has been generated yet, which
// is what the empty object below says to each check. Without it a single
// malformed row throws out of the Content grid's filter and the whole grid stops
// rendering instead of one tile dropping out. The Python half reads content the
// same way (artifact_has_reviewable_content).
function contentFields<T extends object>(artifact: { content: T }): Partial<T> {
  const content: unknown = artifact.content;
  return content !== null && typeof content === "object"
    ? (content as Partial<T>)
    : ({} as Partial<T>);
}

// An artifact the user skipped during campaign setup stays in the process's
// artifact_ids so the choice can be undone, but it never activates — it is not
// content and it is not waiting on anyone.
export function isSkippedInProcess(
  artifact: Artifact,
  process: Process | null,
): boolean {
  return process?.skipped_artifact_ids?.includes(artifact.id) === true;
}

// A proposed campaign's posts have no images yet — those are generated when the
// customer activates the campaign — so they render as broken empty tiles and
// there is nothing to review. A post with no parent campaign, or one whose
// campaign is already active, is real work.
export function isUngeneratedPostInProposedProcess(
  artifact: Artifact,
  process: Process | null,
): boolean {
  return (
    isPost(artifact) &&
    (contentFields(artifact).image_urls?.length ?? 0) === 0 &&
    process?.status === "proposed"
  );
}

// HTML emails are scaffolded at campaign-build time as an empty shell; the body
// only exists once template generation fills in content.instance. Until then
// there is nothing for the customer to approve.
export function isUngeneratedHtmlEmail(artifact: Artifact): boolean {
  return isEmailHtml(artifact) && contentFields(artifact).instance == null;
}

// A plaintext email carries its whole message in content.body, so a row whose
// body is empty or blank is a placeholder the writing agent has not filled in —
// there is no text for the customer to read, let alone approve.
export function isEmptyPlaintextEmail(artifact: Artifact): boolean {
  return isEmailPlaintext(artifact) && !contentFields(artifact).body?.trim();
}

export function isWaitingOnOwner(
  artifact: Artifact,
  process: Process | null,
): boolean {
  // Type first: it is what narrows `status` to the content-status vocabulary.
  // Monitors and proposed edits carry their own unrelated status words.
  if (!isRenderableContentArtifact(artifact)) return false;
  if (!WAITING_ON_OWNER_STATUSES.has(artifact.status)) return false;
  if (isSkippedInProcess(artifact, process)) return false;
  if (isUngeneratedPostInProposedProcess(artifact, process)) return false;
  if (isUngeneratedHtmlEmail(artifact)) return false;
  if (isEmptyPlaintextEmail(artifact)) return false;
  return true;
}
